<template>
  <div>
    <!-- Header -->
    <div class="header bg-gradient-success py-1 py-lg-1 pt-lg-7">
      <div class="separator separator-bottom separator-skew zindex-100">
        <svg x="0" y="0" viewBox="0 0 2560 100" preserveAspectRatio="none" version="1.1"
             xmlns="http://www.w3.org/2000/svg">
          <polygon class="fill-default" points="2560 0 2560 100 0 100"></polygon>
        </svg>
      </div>
    </div>
    <b-container class="mt--6" v-if="alarmError">
      <b-row class="justify-content-center">
        <b-col lg="4" md="7" align="center">
          <b-card no-body class="bg-secondary border-0 mb-0">
            <b-card-body class="px-lg-5 py-lg-5">
              <div>
                <div>
                  Unable to load
                </div>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </b-container>
    <b-container fluid class="mt--6 monitoring-body" v-if="assetMonitoringData">
      <b-row class="monitoring-top">
        <!-- Session details, plus the one destructive action, in a fixed sidebar -->
        <b-col xl="3" lg="4" cols="12">
          <b-card no-body class="bg-secondary border-0 mb-3">
            <b-card-body class="p-4">
              <h2>INFO</h2>
              <div class="info-row" v-if="assetMonitoringData.controlRoomAlarm">
                <span class="info-label">Control room</span>
                <span class="info-value">{{ assetMonitoringData.controlRoomAlarm.controlRoomIdentifier }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Device</span>
                <span class="info-value">
                  {{ assetMonitoringData.deviceName }}
                  <small class="d-block text-muted">{{ assetMonitoringData.deviceIdentifier }}</small>
                </span>
              </div>
              <div class="info-row">
                <span class="info-label">Asset</span>
                <span class="info-value">{{ assetMonitoringData.assetName }}</span>
              </div>
              <div class="info-row" v-if="assetMonitoringData.driverId">
                <span class="info-label">Driver</span>
                <span class="info-value">
                  {{ assetMonitoringData.driverName }}
                  <small class="d-block">
                    <a :href="'tel:' + assetMonitoringData.driverPhone">{{ assetMonitoringData.driverPhone }}</a>
                  </small>
                </span>
              </div>
            </b-card-body>
          </b-card>
          <b-card no-body class="bg-secondary border-0 mb-3">
            <b-card-body class="p-4">
              <h2>SESSION</h2>
              <div class="timeline">
                <div class="timeline-item">
                  <span class="timeline-dot timeline-dot-start"></span>
                  <div class="timeline-head">
                    <span class="timeline-title">Start</span>
                    <span class="timeline-time">{{ $formatTime(new Date(assetMonitoringData.start)) }}</span>
                  </div>
                  <div class="text-muted text-sm">{{ $formatDate(new Date(assetMonitoringData.start)) }}</div>
                  <div class="text-sm">{{ monitoringStartAddress }}</div>
                </div>
                <div class="timeline-item" v-if="assetMonitoringData.end">
                  <span class="timeline-dot timeline-dot-end"></span>
                  <div class="timeline-head">
                    <span class="timeline-title">End</span>
                    <span class="timeline-time">{{ $formatTime(new Date(assetMonitoringData.end)) }}</span>
                  </div>
                  <div class="text-muted text-sm">{{ $formatDate(new Date(assetMonitoringData.end)) }}</div>
                  <div class="text-sm">{{ lastDataPointAddress }}</div>
                </div>
                <div class="timeline-item" v-else-if="lastDataPoint">
                  <span class="timeline-dot timeline-dot-active"></span>
                  <div class="timeline-head">
                    <span class="timeline-title text-danger">Active</span>
                    <span class="timeline-time">{{ $formatTime(new Date(lastDataPoint.timestamp)) }}</span>
                  </div>
                  <div class="text-muted text-sm">{{ $formatDate(new Date(lastDataPoint.timestamp)) }}</div>
                  <div class="text-sm">{{ lastDataPointAddress }}</div>
                </div>
              </div>
              <div class="timeline-summary" v-if="assetMonitoringData.end">
                <span class="info-label">Duration</span>
                <span class="info-value">{{
                    $formatDuration((new Date(assetMonitoringData.end) - new Date(assetMonitoringData.start)) / 1000)
                  }}</span>
              </div>
            </b-card-body>
          </b-card>
          <base-button v-if="!assetMonitoringData.end" type="danger" block class="mb-3"
                       @click.native="endAssetMonitoring()">End monitoring
          </base-button>
        </b-col>
        <!-- Map takes every column the sidebar does not need -->
        <b-col xl="9" lg="8" cols="12" class="map-col">
          <b-card no-body class="bg-secondary border-0 mb-3">
            <b-card-body class="p-4">
              <h2>MAP</h2>
              <GmapMap
                ref="mapRef"
                :center="mapCenter"
                :zoom="16"
                map-type-id="roadmap"
                class="monitoring-map"
                :options='{
                  zoomControl: true,
                  mapTypeControl: true,
                  scaleControl: false,
                  streetViewControl: true,
                  rotateControl: false,
                  fullscreenControl: true,
                  disableDefaultUi: false,
                  styles: [
                    {
                      featureType: "poi",
                      elementType: "all",
                      stylers: [{ visibility: "off" }],
                    }
                  ],
                }'
              >
                <GmapMarker
                  v-if="mapCenter"
                  :position="mapCenter"
                  :clickable="false"
                  :draggable="false"
                  icon="img/markers/Marker_stop_red.png"
                  :zIndex="1"
                />
                <GmapPolyline
                  v-if="route"
                  :path="route"
                  :editable="false"
                  :draggable="false"
                  :options='{
                    strokeColor: "blue"
                  }'
                />
              </GmapMap>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
      <b-row v-if="liveFeedData">
        <b-col cols="12">
          <b-card no-body class="bg-secondary border-0 mb-3">
            <b-card-body class="p-4">
              <div v-if="assetMonitoringData">
                <div v-if="!assetMonitoringData.end"><h2>LIVE</h2></div>
                <div v-else><h2>RECORDED</h2></div>
              </div>
              <div v-for="group in tileGroups" :key="group.key">
                <div class="cycle-heading" v-if="group.timestamp !== null">
                  {{ $formatDate(new Date(group.timestamp)) }} {{ $formatTime(new Date(group.timestamp)) }}
                </div>
                <b-row>
                  <b-col cols="12" md="6" lg="4" xl="3" class="mb-3" align="center"
                         v-for="tile in group.tiles" :key="tile.key">
                    <video autoplay muted playsinline :src="tile.stream" :srcObject.prop="tile.stream" class="camera-tile"
                           @click="videoClicked(tile)" @loadedmetadata="onTileMetadata(tile, $event)" />
                    <div class="font-weight-bold">{{ cameraLabel(tile.camera) }}</div>
                    <div class="text-muted text-sm" v-if="tile.duration !== null">
                      {{ $formatDuration(tile.duration) }}
                    </div>
                  </b-col>
                </b-row>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import crudApi from "../../crud/crud-api";
import "../../janus/adapter.min"
import Janus from "../../janus/janus"

const janusURL = 'https://development.webrtc.rietveld.nl/janus'
// const janusURL = 'https://development.webrtc.rietveld.nl/janus'
// The device restarts recording every time it re-publishes, so one session can
// produce several sets of clips. The cameras of one set start within a second
// or two of each other, while the sets themselves are minutes apart.
const RECORDING_GROUP_WINDOW_MS = 60000
// While the session runs we follow the vehicle live; once it has ended we keep
// polling, more slowly, because the recordings are uploaded one by one and can
// take a while to turn up.
const POLL_ACTIVE_MS = 1000
const POLL_ENDED_MS = 5000
const ICE_SERVERS = [
  {
    urls: "stun:stun.relay.metered.ca:80",
  },
  {
    urls: "turn:global.relay.metered.ca:80",
    username: "0ef57e8df64ba9c7355f8bc2",
    credential: "uagRUvdNbKD//pJt",
  }
]

export default {
  name: "ControlRoom",
  data: _ => ({
    assetMonitoringId: undefined,
    assetMonitoringData: undefined,
    liveFeedData: undefined,
    monitoringStartAddress: undefined,
    lastDataPoint: undefined,
    lastDataPointAddress: undefined,
    mapCenter: undefined,
    mapRef: undefined,
    route: undefined,
    timer: undefined,
    pollIntervalMs: undefined,
    loadedMediaIds: {},
    alarmError: false,
    janus: null,
    janusid: null,
    januspvtid: null,
    janusInitDone: false,
    plugin: null,
    feedStreams: {},
    // One tile per rendered video: { key, stream, camera, feedId, mid, seq }.
    // stream is a MediaStream when live, an object URL when recorded.
    tiles: [],
    tileSeq: 0,
    feedIndex: 0,
    remoteFeedHandles: {},
  }),
  mounted: function () {
    this.init()

    const observer = new MutationObserver(() => {
      this.checkMapRef();
    });

    observer.observe(this.$el, { childList: true, subtree: true });

    // Cleanup when component is destroyed
    this.$once('hook:beforeDestroy', () => {
      observer.disconnect();
    });
  },
  beforeDestroy: function () {
    this.stopPolling()
  },
  watch: {
    assetMonitoringData: function (val) {
      if (val && val.liveFeed && this.liveFeedData !== val.liveFeed) {
        this.liveFeedData = val.liveFeed
      }

      if (val && val.end) {
        this.startPolling(POLL_ENDED_MS)
      }

      if (!this.assetMonitoringData.dataPoints || this.assetMonitoringData.dataPoints.length === 0) {
        return
      }

      this.lastDataPoint = this.assetMonitoringData.dataPoints[this.assetMonitoringData.dataPoints.length-1]

      this.mapCenter = this.getLatLngLiteral(this.lastDataPoint.location)

      let result = []
      for (let r of this.assetMonitoringData.dataPoints) {
        if (r.location !== undefined) {
          result.push(this.getLatLngLiteral(r.location))
        }
      }
      this.route = result

      this.reverseGeocodeData()
    },
    liveFeedData: function () {
      if (!this.assetMonitoringData.end) {
        if (!this.janusInitDone) {
          Janus.init({
            debug: true,
            dependencies: Janus.useDefaultDependencies(),
            callback: () => {
              this.connect(janusURL)
            }
          })

          this.janusInitDone = true
        }
      } else {
        this.loadLiveFeedMedia()
      }
    },
    mapRef: function () {
      if (this.mapCenter) {
        this.mapRef.panTo(this.mapCenter)
      }
    },
    mapCenter: function(val) {
      if (this.mapRef) {
        this.mapRef.panTo(val)
      }
    },
  },
  computed: {
    // One group per recording cycle, oldest first, each ordered by camera.
    // Live tiles carry no timestamp and stay a single group.
    tileGroups() {
      let sorted = [...this.tiles].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0) || a.seq - b.seq)
      let groups = []

      for (let tile of sorted) {
        let group = groups[groups.length - 1]
        let sameCycle = group !== undefined && (tile.timestamp === null
          ? group.timestamp === null
          : group.timestamp !== null && tile.timestamp - group.latest <= RECORDING_GROUP_WINDOW_MS)

        if (sameCycle) {
          group.latest = tile.timestamp
          group.tiles.push(tile)
        } else {
          groups.push({
            key: tile.timestamp === null ? "live" : "cycle:" + tile.timestamp,
            timestamp: tile.timestamp,
            latest: tile.timestamp,
            tiles: [tile],
          })
        }
      }

      for (let group of groups) {
        group.tiles.sort(this.compareCamera)
      }

      return groups
    },
  },
  methods: {
    cameraLabel(camera) {
      return this.$getCameraName(camera) || "Unknown"
    },
    compareCamera(a, b) {
      if (a.camera !== b.camera) {
        if (a.camera === undefined) return 1
        if (b.camera === undefined) return -1

        return a.camera - b.camera
      }

      return a.seq - b.seq
    },
    onTileMetadata(tile, event) {
      // A live MediaStream reports Infinity - only a recorded clip has a length
      if (Number.isFinite(event.target.duration)) {
        tile.duration = event.target.duration
      }
    },
    init() {
      let payload = this.$parseJwt(this.$route.query.t);

      let monitoringData = {
        accessToken: this.$route.query.t,
        monitoring_id: payload.monitoring_id,
      }

      this.assetMonitoringId = payload.monitoring_id

      localStorage.setItem('monitoring', JSON.stringify(monitoringData));

      this.getAssetMonitoring()

      this.startPolling(POLL_ACTIVE_MS)
    },
    startPolling(intervalMs) {
      if (this.pollIntervalMs === intervalMs) {
        return
      }

      this.stopPolling()

      this.pollIntervalMs = intervalMs
      this.timer = setInterval(() => this.getAssetMonitoring(), intervalMs)
    },
    stopPolling() {
      if (this.timer) {
        clearInterval(this.timer)
      }

      this.timer = undefined
      this.pollIntervalMs = undefined
    },
    checkMapRef() {
      if (!this.mapRef && this.$refs.mapRef) {
        this.mapRef = this.$refs.mapRef
      }
    },
    getAssetMonitoring() {
      crudApi.getAssetMonitoring(this.assetMonitoringId).then(json => this.assetMonitoringData = json.data ? json.data : json).catch(error => {
        this.alarmError = true;

        console.log("Error getting asset monitoring data :" + error)
      })
    },
    endAssetMonitoring() {
      crudApi.endAssetMonitoring(this.assetMonitoringData.id)
    },
    // Recordings appear one at a time after the session ends, so every poll
    // picks up whatever is new without re-fetching what is already on screen.
    loadLiveFeedMedia() {
      if (!this.liveFeedData.media) {
        return
      }

      for (let media of this.liveFeedData.media) {
        if (this.loadedMediaIds[media.mediaId]) {
          continue
        }

        this.loadedMediaIds[media.mediaId] = true

        this.getLiveFeedMedia(media)
      }
    },
    // The liveFeed entry only carries a mediaId, so the camera and the mime
    // type have to come from the media object itself.
    getLiveFeedMedia(media) {
      crudApi.getMedia(media.mediaId).then(json => {
        this.downloadLiveFeedMedia(media, json.data ? json.data : json)
      }).catch(error => {
        console.log("Get media error :" + error)

        // Not there yet - let the next poll try again
        delete this.loadedMediaIds[media.mediaId]
      })
    },
    downloadLiveFeedMedia(media, details) {
      let timestamp = details.timestamp ? new Date(details.timestamp).getTime() : NaN

      crudApi.downloadMedia(media.mediaId).then(res => {
        this.tiles.push({
          key: "media:" + media.mediaId,
          stream: URL.createObjectURL(new Blob([res.data], {type: details.mimeType || ""})),
          camera: details.camera !== undefined ? details.camera : media.camera,
          timestamp: isNaN(timestamp) ? null : timestamp,
          duration: null,
          feedId: null,
          mid: null,
          seq: this.tileSeq++,
        })
      }).catch(error => {
        console.log("Download media error :" + error)

        delete this.loadedMediaIds[media.mediaId]
      })
    },
    reverseGeocodeData() {
      if (this.assetMonitoringData.dataPoints !== undefined) {
        this.$reverseGeocode(this.assetMonitoringData.dataPoints[0].location).then(response => {
          this.monitoringStartAddress = response.data.results[0].formatted_address
        })
      } else {
        this.monitoringStartAddress = "Unknown"
      }
      if (this.lastDataPoint !== undefined) {
        this.$reverseGeocode(this.lastDataPoint.location).then(response => {
          this.lastDataPointAddress = response.data.results[0].formatted_address
        })
      } else {
        this.lastDataPointAddress = "Unknown"
      }
    },
    getLatLngLiteral(location) {
      if (location === undefined) {
        return null
      }

      // return new LatLng(location.latitude/100000, location.longitude/100000)
      return { lat: location.latitude/100000, lng: location.longitude/100000 }
      //return new google.maps.LatLng(location.latitude/100000, location.longitude/100000)
    },
    videoClicked(tile) {
      console.log("Video clicked")

      const customMessage = {
        request: "message",
        text: "Enhance video " + (tile.camera === undefined ? "unknown" : tile.camera)
      };

      this.plugin.data({
        message: customMessage,
        success: (result) => {
          Janus.debug('Custom message sent')
        },
        text: (result) => {
          Janus.debug('Text answer : ' + result)
        }
      })
    },
    connect(server) {
      Janus.debug('Connecting to Janus')

      this.janus = new Janus({
        server: server,
        iceServers: ICE_SERVERS,
        success: () => {
          this.attachPlugin()
        },
        error: (error) => {
          this.onError('Failed to connect to janus server', error)
        },
        destroyed: () => {
          window.location.reload()
        }
      })
    },
    attachPlugin() {
      Janus.debug("Janus attach plugin")

      this.janus.attach({
        plugin: "janus.plugin.videoroom",
        success: (plugin) => {
          const self = this;

          Janus.debug("Janus plugin attached")

          this.plugin = plugin

          this.joinRoom(this.liveFeedData.roomId)
        },
        error: (error) => {
          this.onError('Error attaching plugin... ', error)
        },
        onmessage: (msg, jsep) => {
          const self = this;

          let event = msg["videoroom"];

          Janus.debug("Received message of type : " + event)

          if (event) {
            if (event === "joined") {
              // Publisher/manager created, negotiate WebRTC and attach to existing feeds, if any
              this.janusid = msg["id"];
              this.januspvtid = msg["private_id"];

              Janus.debug("Successfully joined room " + msg["room"] + " with ID " + this.janusid);

              if(msg["publishers"]) {
                this.handlePublishers(msg["publishers"]);
              }
            } else if (event === "destroyed") {
              Janus.debug("The room has been destroyed!");

              this.tiles = []
            } else if (event === "event") {
              if(msg["streams"]) {
                let streams = msg["streams"];
                for(let i in streams) {
                  let stream = streams[i];
                  stream["id"] = this.janusid;
                  stream["display"] = "ControlRoom";
                }
                this.feedStreams[this.janusid] = streams;
              } else if(msg["publishers"]) {
                this.handlePublishers(msg["publishers"]);
              } else if (msg["leaving"]) {
                // A device contributes one publisher per camera, so any feed can
                // leave on its own - never assume it is the only one.
                Janus.debug("Leaving: " + msg["leaving"]);

                self.detachRemoteFeed(msg["leaving"]);
              } else if (msg["unpublished"]) {
                // A single camera can fail on its own, and the device unpublishes
                // every camera together on its inactivity timers, then republishes
                // them all when a viewer returns.
                Janus.debug("Unpublished: " + msg["unpublished"]);

                if (msg["unpublished"] === 'ok') {
                  // That would be us, but this handle never publishes anything
                  return;
                }

                self.detachRemoteFeed(msg["unpublished"]);
              } else if(msg["error"]) {
                Janus.debug("Error : " + msg["error"])
              }
            }
          }
          if (jsep) {
            // TODO
          }
        },
      })
    },
    joinRoom(roomId) {
      console.log("Joining room : " + roomId)

      let register = {
        request: "join",
        room: parseInt(roomId),
        ptype: "publisher",
        display: "ControlRoom",
        pin: this.liveFeedData.joinPassphrase,
      }

      this.plugin.send({
        message: register,
        success: (result) => {
          Janus.debug('Janus room joined')
        }
      })
    },
    handlePublishers(list) {
      Janus.debug("Got a list of available publishers/feeds:", list);

      for (let f in list) {
        if (list[f]["dummy"])
          continue;
        let id = list[f]["id"];
        let display = list[f]["display"];
        let streams = list[f]["streams"];
        for (let i in streams) {
          let stream = streams[i];
          stream["id"] = id;
          stream["display"] = display;
        }
        this.feedStreams[id] = streams;

        Janus.debug("  >> [" + id + "] " + display + ":", streams);

        this.newRemoteFeed(id, display, streams);
      }
    },
    // A current device publishes one feed per camera and tags it "cam=<bit>";
    // a legacy device publishes every camera in one feed and sends no
    // description at all, so its cameras stay unidentified.
    cameraFromStreams(streams) {
      for (let i in streams) {
        let match = /cam=(\d+)/.exec(streams[i].description || "");
        if (match) {
          return parseInt(match[1]);
        }
      }

      return undefined;
    },
    detachRemoteFeed(id) {
      let remoteFeed = this.remoteFeedHandles[id];

      // The entry is `true` while the attach is still in flight
      if (remoteFeed && remoteFeed.detach) {
        Janus.debug("Feed " + id + " (" + remoteFeed.rfdisplay + ") is gone, detaching");
        this.removeRemoteFeedStreams(remoteFeed);
        remoteFeed.detach();
      }

      delete this.remoteFeedHandles[id];
      delete this.feedStreams[id];
    },
    removeRemoteFeedStream(remoteFeed, mid) {
      if (!remoteFeed || !remoteFeed.remoteTracks || !remoteFeed.remoteTracks[mid]) {
        return;
      }

      let index = this.tiles.findIndex(t => t.feedId === remoteFeed.feedId && t.mid === mid);
      if (index !== -1) {
        this.tiles.splice(index, 1);
      }
      delete remoteFeed.remoteTracks[mid];
    },
    removeRemoteFeedStreams(remoteFeed) {
      if (!remoteFeed || !remoteFeed.remoteTracks) {
        return;
      }

      Object.keys(remoteFeed.remoteTracks).forEach((mid) => {
        this.removeRemoteFeedStream(remoteFeed, mid);
      });
    },
    newRemoteFeed(id, display, streams) {
      Janus.debug("New remote feed : id=" + id + " ; display=" + display + " ; streams=" + streams)
      // A new feed has been published, create a new plugin handle and attach to it as a subscriber
      const self = this;

      if (self.remoteFeedHandles[id]) {
        Janus.debug("Remote feed already attached for id=" + id);
        return;
      }
      self.remoteFeedHandles[id] = true;

      let remoteFeed = null;
      if (!streams)
        streams = this.feedStreams[id];
      // Each publisher carries exactly one camera, so resolve it once per feed
      let camera = this.cameraFromStreams(streams);
      this.janus.attach(
        {
          plugin: "janus.plugin.videoroom",
          //opaqueId: opaqueId,
          success: function (pluginHandle) {
            remoteFeed = pluginHandle;
            remoteFeed.remoteTracks = {};
            remoteFeed.remoteVideos = 0;
            remoteFeed.simulcastStarted = false;
            remoteFeed.svcStarted = false;
            remoteFeed.feedId = id;
            remoteFeed.camera = camera;
            self.remoteFeedHandles[id] = remoteFeed;
            Janus.debug("Plugin attached! (" + remoteFeed.getPlugin() + ", id=" + remoteFeed.getId() + ")");
            Janus.debug("  -- This is a subscriber");
            // Prepare the streams to subscribe to, as an array: we have the list of
            // streams the feed is publishing, so we can choose what to pick or skip
            let subscription = [];
            for (let i in streams) {
              let stream = streams[i];
              // If the publisher is VP8/VP9 and this is an older Safari, let's avoid video
              if (stream.type === "video" && Janus.webRTCAdapter.browserDetails.browser === "safari" &&
                ((stream.codec === "vp9" && !Janus.safariVp9) || (stream.codec === "vp8" && !Janus.safariVp8))) {

                continue;
              }
              subscription.push({
                feed: stream.id,	// This is mandatory
                mid: stream.mid		// This is optional (all streams, if missing)
              });
              // FIXME Right now, this is always the same feed: in the future, it won't
              remoteFeed.rfid = stream.id;
              remoteFeed.rfdisplay = stream.display;
            }
            // We wait for the plugin to send us an offer
            let subscribe = {
              request: "join",
              room: parseInt(self.liveFeedData.roomId),
              ptype: "subscriber",
              streams: subscription,
              use_msid: false, // TODO ?
              private_id: self.januspvtid,
              pin: self.liveFeedData.joinPassphrase,
            };
            remoteFeed.send({message: subscribe});
          },
          error: function (error) {
            Janus.debug("  -- Error attaching plugin...", error);
            delete self.remoteFeedHandles[id];
          },
          iceState: function (state) {
            Janus.debug("ICE state (feed #" + remoteFeed.rfindex + ") changed to " + state);
          },
          webrtcState: function (on) {
            Janus.debug("Janus says this WebRTC PeerConnection (feed #" + remoteFeed.rfindex + ") is " + (on ? "up" : "down") + " now");
          },
          slowLink: function (uplink, lost, mid) {
            Janus.debug("Janus reports problems " + (uplink ? "sending" : "receiving") +
              " packets on mid " + mid + " (" + lost + " lost packets)");
          },
          onmessage: function (msg, jsep) {
            Janus.debug(" ::: Got a message (subscriber) :::", msg);
            let event = msg["videoroom"];
            Janus.debug("Event: " + event);
            if (msg["error"]) {
              Janus.debug("Alert ! => " + msg["error"]);
            } else if (event) {
              if (event === "attached") {
                // Subscriber created and attached. There is no cap on the number
                // of feeds - rfindex only labels them in the logs.
                remoteFeed.rfindex = ++self.feedIndex;
                Janus.debug("Successfully attached to feed in room " + msg["room"]);
              } else if (event === "event") {
                // Check if we got a simulcast-related event from this publisher
                let substream = msg["substream"];
                let temporal = msg["temporal"];
                if ((substream !== null && substream !== undefined) || (temporal !== null && temporal !== undefined)) {
                  if (!remoteFeed.simulcastStarted) {
                    remoteFeed.simulcastStarted = true;
                  }
                }
                // Or maybe SVC?
                let spatial = msg["spatial_layer"];
                temporal = msg["temporal_layer"];
                if ((spatial !== null && spatial !== undefined) || (temporal !== null && temporal !== undefined)) {
                  if (!remoteFeed.svcStarted) {
                    remoteFeed.svcStarted = true;
                  }
                }
              } else {
                // What has just happened?
              }
            }
            if (jsep) {
              Janus.debug("Handling SDP as well...", jsep);
              let stereo = (jsep.sdp.indexOf("stereo=1") !== -1);
              // Answer and attach
              remoteFeed.createAnswer(
                {
                  jsep: jsep,
                  // Receive remote video explicitly; enable data if offered.
                  tracks: [
                    {type: 'video', recv: true},
                    {type: 'data'}
                  ],
                  customizeSdp: function (jsep) {
                    if (stereo && jsep.sdp.indexOf("stereo=1") == -1) {
                      // Make sure that our offer contains stereo too
                      jsep.sdp = jsep.sdp.replace("useinbandfec=1", "useinbandfec=1;stereo=1");
                    }
                  },
                  success: function (jsep) {
                    Janus.debug("Got SDP!", jsep);
                    let body = {request: "start", room: parseInt(self.liveFeedData.roomId)};
                    remoteFeed.send({message: body, jsep: jsep});
                  },
                  error: function (error) {
                    Janus.debug("WebRTC error:", error);
                  }
                });
            }
          },
          // eslint-disable-next-line no-unused-vars
          onlocaltrack: function (track, on) {
            // The subscriber stream is recvonly, we don't expect anything here
          },
          onremotetrack: function (track, mid, on, metadata) {
            Janus.debug(
              "Remote feed #" + remoteFeed.rfindex +
              ", remote track (mid=" + mid + ") " +
              (on ? "added" : "removed") +
              (metadata ? " (" + metadata.reason + ") " : "") + ":", track
            );
            if (!on) {
              // Track removed, get rid of the stream and the rendering
              if (track.kind === "video") {
                remoteFeed.remoteVideos--;
                if (remoteFeed.remoteVideos === 0) {
                  // no videos
                }
              }
              self.removeRemoteFeedStream(remoteFeed, mid);
              return;
            }
            // If we're here, a new track was added
            if (track.kind === "video" && !remoteFeed.remoteTracks[mid]) {
              // New video track: create a stream out of it
              remoteFeed.remoteVideos++;

              let stream = new MediaStream([track]);
              remoteFeed.remoteTracks[mid] = stream;

              Janus.debug("Created remote video stream");

              self.tiles.push({
                key: id + ":" + mid,
                stream: stream,
                camera: remoteFeed.camera,
                timestamp: null,
                duration: null,
                feedId: id,
                mid: mid,
                seq: self.tileSeq++,
              })
            }
          },
          ondataopen: function() {
            Janus.debug("Data channel is open")
          },
          oncleanup: function () {
            Janus.debug(" ::: Got a cleanup notification (remote feed " + id + ") :::");

            remoteFeed.simulcastStarted = false;
            self.removeRemoteFeedStreams(remoteFeed);
            remoteFeed.remoteTracks = {};
            remoteFeed.remoteVideos = 0;
            delete self.remoteFeedHandles[id];
          }
        })
    }
  }
}
</script>

<style scoped>
/* Tiles scale with their grid column so every camera stays on screen,
   letterboxed on black rather than stretched to the wrong aspect. */
/* Start and end/active as one timeline, so the pair reads as a sequence
   rather than two unrelated cards. */
.timeline-item {
  position: relative;
  padding-left: 1.5rem;
  padding-bottom: 1.25rem;
}

.timeline-item:last-child {
  padding-bottom: 0;
}

.timeline-item:not(:last-child)::before {
  content: "";
  position: absolute;
  left: 0.3125rem;
  top: 1.25rem;
  bottom: 0;
  width: 2px;
  background: rgba(0, 0, 0, 0.08);
}

.timeline-dot {
  position: absolute;
  left: 0;
  top: 0.4375rem;
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
}

.timeline-dot-start { background: #2dce89; }
.timeline-dot-active { background: #f5365c; }
.timeline-dot-end { background: #8898aa; }

.timeline-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.timeline-title {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 0.75rem;
  color: #8898aa;
}

.timeline-time {
  font-weight: 600;
}

.timeline-summary {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  padding-top: 0.75rem;
  margin-top: 0.75rem;
}

/* Label left, value right, one hairline-separated row per field, so the
   sidebar reads as a table instead of a stack of same-weight headings. */
.info-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.info-row:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.info-label {
  color: #8898aa;
  font-size: 0.8125rem;
  white-space: nowrap;
  padding-right: 0.75rem;
}

.info-value {
  font-weight: 600;
  text-align: right;
  word-break: break-word;
}

/* The page fills one screen: the camera row keeps the height it needs and
   the map absorbs whatever is left, so no gap opens up at the bottom. */
.monitoring-body {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 8rem);
}

.monitoring-top {
  flex: 1 1 auto;
  min-height: 0;
}

.map-col,
.map-col > .card,
.map-col > .card > .card-body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.monitoring-map {
  width: 100%;
  flex: 1 1 auto;
  min-height: 320px;
}

.cycle-heading {
  color: #8898aa;
  font-size: 0.8125rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding-bottom: 0.25rem;
  margin-bottom: 0.75rem;
}

.camera-tile {
  width: 100%;
  max-height: 28vh;
  min-height: 160px;
  object-fit: contain;
  background: #000;
  cursor: pointer;
}
</style>
